import { Card } from '@/components/shared/card/Card';
import { useMyLocation } from '@/services/useLocations';
import { useSelectedDate } from '@/services/useSelectedDate';
import { FC, Suspense, useCallback, useMemo } from 'react';
import { FiPlus } from 'react-icons/fi';
import uuid from 'react-uuid';
import { ErrorMessage } from '../../components/errors/ErrorMessage';
import { GanttMarkerLine, GanttProvider } from '../../components/gantt/gantt/GanttProvider';
import { GanttTimerBar } from '../../components/gantt/gantt/GanttTimerBar';
import { Loading } from '../../components/shared/Loading';
import { EMPLOYEE_DETAILS_COLUMN_WIDTH, EMPLOYEE_NAME_COLUMN_WIDTH } from '../../helpers/constants';
import { getDayKey, secondsToHours, secondsToTimeString } from '../../helpers/dateHelper';
import { useSystemSettings } from '../../services/settings/SystemSettingsProvider';
import { OpenClosingTimeOptions } from '../../services/settings/systemSettings.types';
import { useOpenClosingTimePref } from '../../services/settings/useOpenClosingTimePref';
import { useLocationDefaultTimes } from '../admin/locations/edit/useLocationDefaultTimes';
import { RosterHourSummary } from './RosterHourSummary';
import { RosterShifts } from './RosterShifts';
import { DailyTotalHeader } from './daily/DailyTotalHeader';
import { RosterDrawer } from './drawer/RosterDrawer';
import { useSubsections } from './hooks/useSubsections';
import { MonthlyTotalHeader } from './monthly/MonthlyTotalHeader';
import { useWorkingRoster } from './providers/WorkingRosterService';
import strings from './roster.translations';
import { shiftsWrapperId } from './staffPicker/rosterPickerHelper';

export const Roster: FC = () => {
  const { id: locationId } = useMyLocation();
  const { defaultShiftTimes } = useLocationDefaultTimes(locationId);

  const minuteIncrement = useSystemSettings(state => state.settings.minuteIncrement);

  const { workingRoster, setWorkingRoster } = useWorkingRoster();

  const selectedDate = useSelectedDate();
  const isInPast = new Date() > selectedDate;

  const defaultShiftTimesForDay = defaultShiftTimes[getDayKey(selectedDate)];
  const minShiftDuration = useSystemSettings(state => state.settings.minShiftDuration);

  const subsections = useSubsections();

  const hoursCovered = workingRoster ? secondsToHours(workingRoster.endTime - workingRoster.startTime) : 0;

  const openClosingTimesPref = useOpenClosingTimePref();

  const showOpeningHours = useMemo(() => {
    if (!workingRoster) return false;

    if (typeof workingRoster.showOpeningHours === 'boolean') {
      return workingRoster.showOpeningHours;
    }

    return openClosingTimesPref === OpenClosingTimeOptions.all;
  }, [workingRoster, openClosingTimesPref]);

  const markerLines: GanttMarkerLine[] = useMemo(() => {
    if (!showOpeningHours) {
      return [];
    }

    const lines: GanttMarkerLine[] = [];

    if (workingRoster?.openTime) {
      lines.push({
        time: workingRoster.openTime,
        color: '#22c55e',
        label: `Opening Time - opens at ${secondsToTimeString(workingRoster.openTime)}, roster starts at ${secondsToTimeString(workingRoster.startTime)}`
      });
    }

    if (workingRoster?.closeTime) {
      lines.push({
        time: workingRoster.closeTime,
        color: '#ef4444',
        label: `Closing Time - closes at ${secondsToTimeString(workingRoster.closeTime)}, roster ends at ${secondsToTimeString(workingRoster.endTime)}`
      });
    }

    return lines;
  }, [workingRoster, showOpeningHours]);

  const addShift = useCallback(() => {
    if (!workingRoster) {
      throw Error('Working Roster should be set');
    }

    const shifts = [...workingRoster.shifts];
    shifts.push({
      id: `CREATE-${uuid()}`,
      start: defaultShiftTimesForDay.start,
      end: defaultShiftTimesForDay.end,
      breaks: [],
      tasks: []
    });
    setWorkingRoster({ ...workingRoster, shifts });
  }, [setWorkingRoster, workingRoster]);

  if (!workingRoster && isInPast) {
    return <ErrorMessage message={strings.noRoster}></ErrorMessage>;
  } else if (!workingRoster) {
    return <Card className="max-w-4xl mx-auto mt-5 p-5">{strings.noRoster}</Card>;
  } else {
    return (
      <>
        <GanttProvider
          containerId={shiftsWrapperId}
          markerLines={markerLines}
          subsections={subsections}
          minTime={workingRoster.startTime}
          maxTime={workingRoster.endTime}
          minMainLength={minShiftDuration}
          minuteIncrement={minuteIncrement}
        >
          <div className="flex-1 flex flex-col overflow-hidden" id={shiftsWrapperId}>
            <div className="overflow-scroll relative bg-white">
              <div style={{ minWidth: 100 * hoursCovered }}>
                <Suspense fallback={<Loading />}>
                  <div className="flex sticky top-0 z-[100] bg-white">
                    <div
                      className="border-b border-r px-3 sticky left-0 z-[60] flex items-center text-green-600 font-bold gap-3"
                      style={{ width: EMPLOYEE_NAME_COLUMN_WIDTH }}
                    >
                      <div className="border-b-[3px] w-full border-green-600">{strings.employees}</div>
                      <button
                        className="bg-green-600 h-6 w-6 rounded-full flex items-center justify-center"
                        onClick={addShift}
                      >
                        <FiPlus className="h-6 w-6 text-white" />
                      </button>
                    </div>
                    <div className="flex border-b" style={{ width: EMPLOYEE_DETAILS_COLUMN_WIDTH }}>
                      <div className="flex-1 flex border-r">
                        <MonthlyTotalHeader />
                      </div>
                      <div className="flex-1 flex">
                        <DailyTotalHeader />
                      </div>
                    </div>
                    <div className="flex-1 bg-white">
                      <GanttTimerBar />
                    </div>
                  </div>
                  <RosterShifts workingRoster={workingRoster} setWorkingRoster={setWorkingRoster} />

                  <RosterHourSummary />
                </Suspense>
              </div>
            </div>
            <div className="flex-1"></div>
            <RosterDrawer />
          </div>
        </GanttProvider>
      </>
    );
  }
};
